/**
 * Student 360 — Minimal Canonical Shell (2026-09, FIN1 2026-02, ATT1 2026-09)
 *
 * Architecture compliance:
 *   CANONICAL PG sections: identity, student number, lifecycle status, admission date,
 *                           program enrollments, finance (FIN1), attendance (ATT1)
 *   NOT CONNECTED sections: advising, exam history, credentials (marked explicitly)
 *
 * Data paths:
 *   GET /api/core/students/:id                    → StudentService → PostgreSQL
 *   GET /api/core/students/:id/finance            → FinanceService → PostgreSQL (FIN1)
 *   GET /api/core/students/:id/finance/charges    → FinanceService → PostgreSQL (FIN1)
 *   GET /api/core/students/:id/finance/payments   → FinanceService → PostgreSQL (FIN1)
 *   GET /api/attendance/terms                     → AcademicAttendanceService → PostgreSQL (ATT1)
 *   GET /api/attendance/student/:id/summary       → AcademicAttendanceService → PostgreSQL (ATT1)
 *
 * Cross-tenant behavior: backend returns 404 for cross-institution access (no existence leakage).
 *
 * Dev mode shows source badges. Production users see no database terminology.
 * Finance section: no source-system badge (FIN1 spec).
 * Attendance section: no source-system badge (ATT1 — canonical, no TRANSITIONAL label).
 *
 * ATT1 UI truth rules:
 *   - attendance_percentage = null → render "N/A" (not 0%) per INV-A7
 *   - EXCUSED, LATE, UNMARKED shown with explicit labels
 *   - Cancelled sessions shown as informational only (excluded from eligible)
 *   - No legacy /v1/core/attendance MongoDB call
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader } from "../components/layout/Shell";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Loader2, AlertTriangle, UserCircle, GraduationCap,
  CalendarDays, BookOpen, Wallet, Clock, Database, TrendingDown,
  ChevronDown, ChevronRight, BarChart2, RefreshCw,
} from "lucide-react";
import { api } from "../lib/api";
import { useInstitution } from "../context/InstitutionContext";

const IS_DEV = process.env.NODE_ENV !== "production";

// ---------------------------------------------------------------------------
// Source badge (dev-only)
// ---------------------------------------------------------------------------
function SourceBadge({ source }) {
  if (!IS_DEV) return null;
  const styles = {
    CANONICAL_PG: "text-emerald-700 border-emerald-300 bg-emerald-50",
    TRANSITIONAL: "text-amber-700 border-amber-300 bg-amber-50",
    NOT_CONNECTED: "text-gray-500 border-gray-300 bg-gray-50",
  };
  return (
    <Badge variant="outline" className={`text-[9px] ${styles[source] || styles.NOT_CONNECTED}`}>
      {source.replace("_", " ")}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
function SectionHeader({ icon: Icon, title, source }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </div>
      <SourceBadge source={source} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status badge for lifecycle
// ---------------------------------------------------------------------------
function LifecycleBadge({ status }) {
  const map = {
    ACTIVE: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
    GRADUATED: "bg-blue-500/15 text-blue-700 border-blue-500/30",
    ENROLLED: "bg-sky-500/15 text-sky-700 border-sky-500/30",
    LEAVE: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    SUSPENDED: "bg-red-500/15 text-red-700 border-red-500/30",
    WITHDRAWN: "bg-gray-500/15 text-gray-600 border-gray-500/30",
    ALUMNUS: "bg-purple-500/15 text-purple-700 border-purple-500/30",
  };
  const cls = map[status] || "bg-gray-100 text-gray-600";
  return (
    <Badge variant="outline" className={cls} data-testid="student-360-lifecycle-status">
      {status}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Not-connected section
// ---------------------------------------------------------------------------
function NotConnectedSection({ icon: Icon, title }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-5">
      <SectionHeader icon={Icon} title={title} source="NOT_CONNECTED" />
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        Not yet connected — backend integration planned.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Money formatter — Decimal string → locale-aware currency string
// ---------------------------------------------------------------------------
function formatMoney(amount, currency) {
  const num = parseFloat(amount ?? 0);
  if (isNaN(num)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: !currency || currency === "N/A" ? "INR" : currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency ?? ""} ${num.toFixed(2)}`;
  }
}

// ---------------------------------------------------------------------------
// Canonical Attendance section (ATT1 — reads from A3 canonical PG endpoints)
//
//  States: loading_terms | terms_error | no_terms |
//          loading_summary | loaded | no_data | error
//  Sources:
//    GET /api/attendance/terms                        → list available academic terms
//    GET /api/attendance/student/:id/summary?term_id  → 9 canonical metrics (A1-F §10)
//
//  ATT1 UI truth rules (frozen by INV-A7):
//    - attendance_percentage = null  →  "N/A"  (never "0%")
//    - EXCUSED excluded from percentage denominator (EXCLUSIVE model)
//    - LATE counted as attended in summary (label shown explicitly)
//    - UNMARKED = eligible − (attended + absent + late + excused)
//    - Cancelled sessions informational only (excluded from eligible count)
//  Legacy /v1/core/attendance MongoDB endpoint is NOT called here.
// ---------------------------------------------------------------------------
function AttendanceSection({ studentId }) {
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState(null);
  const [termsState, setTermsState] = useState("loading");
  const [attState, setAttState] = useState({ status: "idle" });
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Load available terms on mount
  useEffect(() => {
    setTermsState("loading");
    api.get("/attendance/terms")
      .then(({ data }) => {
        const list = data.terms ?? [];
        setTerms(list);
        if (list.length > 0) {
          setSelectedTermId(list[0].id);   // default = newest term
          setTermsState("loaded");
        } else {
          setTermsState("no_terms");
        }
      })
      .catch(() => setTermsState("error"));
  }, []);

  // Load summary whenever student or term changes
  const fetchSummary = useCallback(() => {
    if (!studentId || !selectedTermId) return;
    setAttState({ status: "loading" });
    api.get(`/attendance/student/${studentId}/summary`, { params: { term_id: selectedTermId } })
      .then(({ data }) => setAttState({ status: "loaded", data }))
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 404) {
          setAttState({ status: "no_data" });
        } else {
          setAttState({
            status: "error",
            message: err?.response?.data?.detail ?? "Attendance data unavailable.",
          });
        }
      });
  }, [studentId, selectedTermId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const header = (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <CalendarDays className="h-3.5 w-3.5" />
        Attendance
      </div>
      {attState.status === "loaded" && (
        <button
          onClick={fetchSummary}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title="Refresh"
          data-testid="attendance-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  // Term selector (shown once terms are loaded)
  const termSelector = terms.length > 1 ? (
    <div className="mb-3">
      <select
        value={selectedTermId ?? ""}
        onChange={(e) => setSelectedTermId(e.target.value)}
        className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground w-full max-w-xs"
        data-testid="attendance-term-select"
      >
        {terms.map((t) => (
          <option key={t.id} value={t.id}>{t.display}</option>
        ))}
      </select>
    </div>
  ) : null;

  // ── States ──────────────────────────────────────────────────────────────
  if (termsState === "loading") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-attendance-section">
        {header}
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="attendance-loading-terms">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading terms…
        </div>
      </div>
    );
  }

  if (termsState === "error") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-attendance-section">
        {header}
        <div className="flex items-center gap-2 text-xs text-destructive py-2" data-testid="attendance-terms-error">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Unable to load academic terms.
        </div>
      </div>
    );
  }

  if (termsState === "no_terms") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-attendance-section">
        {header}
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="attendance-no-terms">
          <Database className="h-3.5 w-3.5" /> No academic terms found for this institution.
        </div>
      </div>
    );
  }

  if (attState.status === "loading" || attState.status === "idle") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-attendance-section">
        {header}
        {termSelector}
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="attendance-loading">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading attendance summary…
        </div>
      </div>
    );
  }

  if (attState.status === "no_data") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-attendance-section">
        {header}
        {termSelector}
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="attendance-no-data">
          <Database className="h-3.5 w-3.5" /> No attendance records found for this term.
        </div>
      </div>
    );
  }

  if (attState.status === "error") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-attendance-section">
        {header}
        {termSelector}
        <div className="flex items-center gap-2 text-xs text-destructive py-2" data-testid="attendance-error">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {attState.message}
        </div>
      </div>
    );
  }

  // ── status === "loaded" ──────────────────────────────────────────────────
  const d = attState.data;

  // INV-A7: null percentage must render as "N/A", never "0%"
  const pctDisplay = d.attendance_percentage != null
    ? `${d.attendance_percentage}%`
    : "N/A";

  const pctColor = d.attendance_percentage == null
    ? "text-muted-foreground"
    : parseFloat(d.attendance_percentage) >= 75
      ? "text-emerald-600 dark:text-emerald-400"
      : parseFloat(d.attendance_percentage) >= 50
        ? "text-amber-600 dark:text-amber-400"
        : "text-destructive";

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-attendance-section">
      {header}
      {termSelector}

      {/* ── Attendance percentage hero metric ── */}
      <div className="flex items-baseline gap-2 mb-4">
        <span
          className={`text-3xl font-bold tabular-nums ${pctColor}`}
          data-testid="attendance-percentage"
        >
          {pctDisplay}
        </span>
        <span className="text-xs text-muted-foreground">
          {d.attendance_percentage == null
            ? "No eligible attended/absent sessions (INV-A7)"
            : "attendance rate (PRESENT + LATE / eligible)"}
        </span>
      </div>

      {/* ── 9 canonical metrics grid ── */}
      <dl className="grid grid-cols-3 gap-2 text-sm mb-4" data-testid="attendance-metrics">
        <MetricCell label="Eligible" value={d.eligible_sessions} testId="attendance-eligible" />
        <MetricCell label="Attended" value={d.attended_sessions} testId="attendance-attended" />
        <MetricCell label="Absent" value={d.absent_sessions} testId="attendance-absent" />
        <MetricCell label="Late" value={d.late_sessions} testId="attendance-late" muted />
        <MetricCell label="Excused" value={d.excused_sessions} testId="attendance-excused" muted />
        <MetricCell label="Unmarked" value={d.unmarked_sessions} testId="attendance-unmarked" muted />
      </dl>

      {/* ── Cancelled + latest date ── */}
      <div className="text-[11px] text-muted-foreground space-y-0.5 mb-3" data-testid="attendance-meta">
        {d.cancelled_sessions > 0 && (
          <div data-testid="attendance-cancelled">
            {d.cancelled_sessions} session{d.cancelled_sessions !== 1 ? "s" : ""} cancelled (excluded from eligible)
          </div>
        )}
        {d.latest_attendance_date && (
          <div data-testid="attendance-latest-date">
            Last marked: {d.latest_attendance_date}
          </div>
        )}
        {!d.latest_attendance_date && d.eligible_sessions > 0 && (
          <div data-testid="attendance-never-marked" className="italic">
            No marks recorded for this term.
          </div>
        )}
      </div>

      {/* ── Section breakdown (collapsible) ── */}
      {d.section_breakdown?.length > 0 && (
        <div>
          <button
            onClick={() => setShowBreakdown((p) => !p)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="attendance-breakdown-toggle"
          >
            {showBreakdown ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Section breakdown ({d.section_breakdown.length})
          </button>
          {showBreakdown && (
            <div className="mt-2 space-y-2" data-testid="attendance-section-breakdown">
              {d.section_breakdown.map((sec, i) => {
                const secPct = sec.attendance_percentage != null
                  ? `${sec.attendance_percentage}%`
                  : "N/A";
                return (
                  <div
                    key={sec.section_id}
                    className="rounded border border-border bg-muted/20 px-3 py-2 text-xs"
                    data-testid={`attendance-section-row-${i}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-muted-foreground truncate max-w-[180px]">
                        {sec.section_id.slice(0, 8)}…
                      </span>
                      <span
                        className={`font-semibold tabular-nums ${
                          sec.attendance_percentage == null
                            ? "text-muted-foreground"
                            : parseFloat(sec.attendance_percentage) >= 75
                              ? "text-emerald-600"
                              : "text-amber-600"
                        }`}
                        data-testid={`attendance-section-pct-${i}`}
                      >
                        {secPct}
                      </span>
                    </div>
                    <div className="text-muted-foreground mt-1">
                      {sec.eligible_sessions} eligible · {sec.attended_sessions} attended
                      {sec.excused_sessions > 0 && ` · ${sec.excused_sessions} excused`}
                      {sec.unmarked_sessions > 0 && ` · ${sec.unmarked_sessions} unmarked`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Small metric cell for the 9-metric grid
function MetricCell({ label, value, testId, muted = false }) {
  return (
    <div className={`rounded-md border border-border px-2.5 py-2 ${muted ? "bg-muted/10" : "bg-muted/30"}`}>
      <dt className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd
        className={`font-semibold text-sm mt-0.5 tabular-nums ${muted ? "text-muted-foreground" : ""}`}
        data-testid={testId}
      >
        {value ?? 0}
      </dd>
    </div>
  );
}

// Canonical Finance section (FIN1 — reads from PG canonical endpoints)
//
//  States: loading | no_account | loaded | multi_currency_error | error
//  Sources:
//    GET /api/core/students/:id/finance         → summary
//    GET /api/core/students/:id/finance/charges  → charge list
//    GET /api/core/students/:id/finance/payments → payment list
//
//  No source-system badge shown (FIN1 spec).
//  Legacy /v1/core/fees endpoint is NOT called here.
// ---------------------------------------------------------------------------
function FinanceSection({ studentId }) {
  const [finState, setFinState] = useState({ status: "loading" });

  useEffect(() => {
    if (!studentId) return;
    setFinState({ status: "loading" });

    api
      .get(`/core/students/${studentId}/finance`)
      .then(({ data: summary }) =>
        Promise.all([
          api.get(`/core/students/${studentId}/finance/charges`),
          api.get(`/core/students/${studentId}/finance/payments`),
        ]).then(([{ data: chargesPayload }, { data: paymentsPayload }]) => {
          setFinState({
            status: "loaded",
            summary,
            charges: chargesPayload.items ?? [],
            payments: paymentsPayload.items ?? [],
          });
        })
      )
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 404) {
          setFinState({ status: "no_account" });
        } else if (code === 422) {
          setFinState({
            status: "multi_currency_error",
            message:
              err?.response?.data?.detail ??
              "Multi-currency account — canonical aggregate balance unavailable.",
          });
        } else {
          setFinState({
            status: "error",
            message:
              err?.response?.data?.detail ?? "Finance data unavailable.",
          });
        }
      });
  }, [studentId]);

  const header = (
    <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      <Wallet className="h-3.5 w-3.5" />
      Finance
    </div>
  );

  if (finState.status === "loading") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-finance-section">
        {header}
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="finance-loading">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading finance data…
        </div>
      </div>
    );
  }

  if (finState.status === "no_account") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-finance-section">
        {header}
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="finance-no-account">
          <Database className="h-3.5 w-3.5" />
          No finance account found for this student at this institution.
        </div>
      </div>
    );
  }

  if (finState.status === "multi_currency_error") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-finance-section">
        {header}
        <div className="flex items-start gap-2 text-xs py-2" data-testid="finance-multi-currency-error">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600 mt-0.5" />
          <div>
            <div className="font-medium text-amber-700">Multi-currency account</div>
            <div className="text-muted-foreground mt-0.5">
              Canonical aggregate balance unavailable. Use the charges and
              payments sections below for itemized data.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (finState.status === "error") {
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-finance-section">
        {header}
        <div className="flex items-center gap-2 text-xs text-destructive py-2" data-testid="finance-error">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {finState.message}
        </div>
      </div>
    );
  }

  // ── status === "loaded" ──────────────────────────────────────────────────
  const { summary, charges, payments } = finState;
  const currency = summary.currency;
  const outstanding = parseFloat(summary.outstanding_balance ?? 0);
  const unapplied = parseFloat(summary.unapplied_cash ?? 0);

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-finance-section">
      {header}

      {/* ── Summary KPIs ── */}
      <dl className="grid grid-cols-3 gap-2 text-sm mb-4" data-testid="finance-summary">
        <div className="rounded-md border border-border bg-muted/30 px-2.5 py-2">
          <dt className="text-[10px] text-muted-foreground uppercase tracking-wide">Outstanding</dt>
          <dd
            className={`font-semibold text-sm mt-0.5 ${outstanding > 0 ? "text-destructive" : "text-emerald-600"}`}
            data-testid="finance-outstanding-balance"
          >
            {formatMoney(summary.outstanding_balance, currency)}
          </dd>
        </div>
        <div className="rounded-md border border-border bg-muted/30 px-2.5 py-2">
          <dt className="text-[10px] text-muted-foreground uppercase tracking-wide">Unapplied Cash</dt>
          <dd className="font-semibold text-sm mt-0.5" data-testid="finance-unapplied-cash">
            {unapplied > 0 ? formatMoney(summary.unapplied_cash, currency) : "—"}
          </dd>
        </div>
        <div className="rounded-md border border-border bg-muted/30 px-2.5 py-2">
          <dt className="text-[10px] text-muted-foreground uppercase tracking-wide">Account</dt>
          <dd className="font-medium text-sm mt-0.5" data-testid="finance-account-status">
            {summary.account_status}
          </dd>
        </div>
      </dl>

      {/* ── Charges ── */}
      <div className="mb-3" data-testid="finance-charges-section">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
          Charges ({charges.length})
        </div>
        {charges.length === 0 ? (
          <div className="text-xs text-muted-foreground py-1" data-testid="finance-no-charges">
            No finalized charges.
          </div>
        ) : (
          <div className="space-y-1.5">
            {charges.map((ch) => (
              <div
                key={ch.id}
                className="flex items-start justify-between text-xs border border-border rounded px-2.5 py-2"
                data-testid={`finance-charge-row`}
              >
                <div className="flex-1 min-w-0 pr-2">
                  <div className="font-medium truncate">{ch.description || "Charge"}</div>
                  <div className="text-muted-foreground mt-0.5 font-mono">
                    {ch.charge_date}
                    {ch.due_date && ` · due ${ch.due_date}`}
                  </div>
                </div>
                <div className="text-right shrink-0 mr-2">
                  <div className="font-mono font-medium">
                    {formatMoney(ch.original_amount, ch.currency)}
                  </div>
                  {parseFloat(ch.open_amount) < parseFloat(ch.original_amount) && (
                    <div className="text-muted-foreground text-[10px]">
                      open {formatMoney(ch.open_amount, ch.currency)}
                    </div>
                  )}
                </div>
                <Badge variant="outline" className="text-[9px] shrink-0">{ch.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Payments ── */}
      <div data-testid="finance-payments-section">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
          Payments ({payments.length})
        </div>
        {payments.length === 0 ? (
          <div className="text-xs text-muted-foreground py-1" data-testid="finance-no-payments">
            No payment history.
          </div>
        ) : (
          <div className="space-y-1.5">
            {payments.map((pmt) => (
              <div
                key={pmt.id}
                className="flex items-start justify-between text-xs border border-border rounded px-2.5 py-2"
                data-testid={`finance-payment-row`}
              >
                <div className="flex-1 min-w-0 pr-2">
                  <div className="font-medium">{pmt.payment_method.replace(/_/g, " ")}</div>
                  <div className="text-muted-foreground mt-0.5 font-mono">
                    {pmt.payment_date}
                    {pmt.reference_number && ` · ${pmt.reference_number}`}
                  </div>
                </div>
                <div className="text-right shrink-0 mr-2">
                  <div className="font-mono font-medium">
                    {formatMoney(pmt.payment_amount, pmt.currency)}
                  </div>
                  {parseFloat(pmt.unapplied_amount) > 0 && (
                    <div className="text-muted-foreground text-[10px]">
                      unapplied {formatMoney(pmt.unapplied_amount, pmt.currency)}
                    </div>
                  )}
                </div>
                <Badge variant="outline" className="text-[9px] shrink-0">{pmt.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function StudentProfile() {
  const { studentId } = useParams();
  const { current } = useInstitution();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Legacy MongoDB attendance state removed — ATT1 canonical AttendanceSection handles this.
  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    setError(null);

    api.get(`/core/students/${studentId}`)
      .then(({ data }) => setStudent(data))
      .catch((e) => {
        setError(
          e?.response?.status === 404
            ? "Student not found."
            : e?.response?.data?.detail || "Could not load student."
        );
      })
      .finally(() => setLoading(false));
  }, [studentId]);

  if (!current) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="student-360-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8" data-testid="student-360-error">
        <div className="flex items-center gap-2 text-sm text-destructive mb-4">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
        <Link to="/core/students">
          <Button variant="ghost" size="sm" className="gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Students
          </Button>
        </Link>
      </div>
    );
  }

  if (!student) return null;

  const displayName = student.identity?.display_name
    || `${student.identity?.given_name || ""} ${student.identity?.family_name || ""}`.trim()
    || student.student_number;

  return (
    <div data-testid="student-360-page">
      <PageHeader
        eyebrow="Student & Academic Ops · Student 360"
        title={displayName}
        description={`${student.student_number} · Canonical student profile`}
        actions={
          <div className="flex items-center gap-2">
            <LifecycleBadge status={student.lifecycle_status} />
            {IS_DEV && <SourceBadge source="CANONICAL_PG" />}
            <Link to="/core/students">
              <Button variant="ghost" size="sm" className="gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" /> Students
              </Button>
            </Link>
          </div>
        }
      />

      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ---- LEFT: Canonical identity + academic context ---- */}
        <div className="lg:col-span-2 space-y-5">

          {/* Identity — CANONICAL PG */}
          <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-identity-section">
            <SectionHeader icon={UserCircle} title="Identity" source="CANONICAL_PG" />
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">Full name</dt>
                <dd className="font-medium mt-0.5" data-testid="student-360-display-name">{displayName}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Student number</dt>
                <dd className="font-mono mt-0.5" data-testid="student-360-student-number">{student.student_number}</dd>
              </div>
              {student.identity?.given_name && (
                <div>
                  <dt className="text-xs text-muted-foreground">Given name</dt>
                  <dd className="mt-0.5">{student.identity.given_name}</dd>
                </div>
              )}
              {student.identity?.family_name && (
                <div>
                  <dt className="text-xs text-muted-foreground">Family name</dt>
                  <dd className="mt-0.5">{student.identity.family_name}</dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-muted-foreground">Lifecycle status</dt>
                <dd className="mt-0.5"><LifecycleBadge status={student.lifecycle_status} /></dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">Admission date</dt>
                <dd className="mt-0.5 font-mono text-xs" data-testid="student-360-admission-date">
                  {student.admission_date || "—"}
                </dd>
              </div>
            </dl>
          </div>

          {/* Program Enrollments — CANONICAL PG */}
          <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-program-section">
            <SectionHeader icon={GraduationCap} title="Program Enrollments" source="CANONICAL_PG" />
            {student.program_enrollments?.length > 0 ? (
              <ul className="space-y-2">
                {student.program_enrollments.map((pe) => (
                  <li key={pe.id} className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2">
                    <div>
                      <span className="font-mono text-xs text-muted-foreground">{pe.id.slice(0, 8)}…</span>
                      <span className="ml-2 text-xs">
                        {pe.effective_from && `from ${pe.effective_from}`}
                        {pe.effective_to && ` to ${pe.effective_to}`}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{pe.status}</Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-muted-foreground py-2 flex items-center gap-1.5">
                <Database className="h-3.5 w-3.5" />
                No program enrollments in canonical store yet.
                <span className="opacity-60">(Enrollment migration planned under Wave S3.)</span>
              </div>
            )}
          </div>

          {/* Attendance — CANONICAL PG (ATT1) */}
          <AttendanceSection studentId={studentId} />

          {/* Finance — CANONICAL PG (FIN1) */}
          <FinanceSection studentId={studentId} />
        </div>

        {/* ---- RIGHT: Summary card + not-connected sections ---- */}
        <div className="space-y-5">
          {/* Summary card */}
          <div className="rounded-lg border border-border bg-card p-5" data-testid="student-360-summary-card">
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                <UserCircle className="h-7 w-7 text-muted-foreground" />
              </div>
              <div className="font-semibold text-base">{displayName}</div>
              <div className="font-mono text-xs text-muted-foreground mt-0.5">{student.student_number}</div>
              <div className="mt-2"><LifecycleBadge status={student.lifecycle_status} /></div>
            </div>
            <dl className="space-y-2 text-xs pt-3 border-t border-border">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Institution</dt>
                <dd className="font-medium">{current.short_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Enrollments</dt>
                <dd>{student.program_enrollments?.length || 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Canonical source</dt>
                <dd><SourceBadge source="CANONICAL_PG" /></dd>
              </div>
            </dl>
          </div>

          {/* Not connected sections */}
          <NotConnectedSection icon={BookOpen} title="Courses & Grades" />
          <NotConnectedSection icon={CalendarDays} title="Exam History" />
          <NotConnectedSection icon={GraduationCap} title="Credentials" />
        </div>
      </div>
    </div>
  );
}
